import { useContext, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";
import { setFlowElements } from "../../../actions/taskSidebar";
import { notifyIcon, number, quote } from "../../../config";
import { addWorkflowElement } from "../../../shared/services/workflow.services";
import { getNotification, isNonNegativeOrNull } from "../../../utils/common";
import { GridContext } from "../../../utils/kendo";
import { generateRandomId } from "../../TaskSidebar/TaskSidebarContent/Attachments/attachments.helper";
import { checkFlowInsertionFromFlows, handleTaskState } from "../../TaskSidebar/sidebar.helper";
import { FlowElementsContext } from "../../Workflow/FlowElements/FlowElementsContextProvider";
import { update } from "../../Workflow/workflowHelper";

export const useFlowElements = () => {
  const { defaultDetails } = useSelector((state) => state.tasks);
  const { isNewTask, flowElements } = useSelector((state) => state.taskSidebar);
  const { projectId, workflowId } = useParams();
  const dispatch = useDispatch();

  const { flowElementData, setFlowElementData, existingFlowElements, entity, addFlowElement, editFlowElement, taskElement } = useContext(GridContext);

  const {
    flowAssignmentType,
    flowWorkflowAssignmentType,
    flowTaskAssignmentId,
    flowWorkflowAssignmentId,
    flowElementDueDate,
    flowElementDelay,
    isApproval,
    isFlowLocked,
  } = useContext(FlowElementsContext);

  const projectWorkflowOrUser = flowAssignmentType?.value == number.THREE 
    ? (flowWorkflowAssignmentId === null || (flowWorkflowAssignmentType && flowWorkflowAssignmentId)) 
    : true;

  const requiredFields = (isDynamicFlow) => {
    if (isDynamicFlow) {
      return isNonNegativeOrNull(flowElementDueDate) && isNonNegativeOrNull(flowElementDelay);
    } else {
      return (
        flowAssignmentType &&
        flowTaskAssignmentId &&
        projectWorkflowOrUser &&
        isNonNegativeOrNull(flowElementDueDate) &&
        isNonNegativeOrNull(flowElementDelay)
      );
    }
  };

  const assignmentProjectIdCondition =
    flowTaskAssignmentId?.AssignmentType === number.THREE
      ? flowTaskAssignmentId?.value
      : defaultDetails?.allProjectsList?.find((item) => item.IsPersonal === number.ONE && item.CreatedBy === flowTaskAssignmentId?.value)?.ProjectId;

  const createPayload = (isNewElement, dataItem) => {
    let payload = dataItem.dynamicFlow
      ? {
          variableId: dataItem?.selectedActiveMember?._id,
          flowElementId: dataItem?.id,
          flowElementType: 2,
          dueDateAfter: flowElementDueDate,
          delayAfterCompletion: flowElementDelay,
          sortId: isNewElement ? flowElementData?.length : dataItem?.sortId,
          isApproval,
          isFlowLocked,
          assignBasedOnCreator: dataItem?.profileType === "ticketCreator" ? 1 : 0,
        }
      : {
          flowElementId: dataItem?.id,
          flowElementType: 1,
          taskAssignmentType: flowTaskAssignmentId?.AssignmentType,
          taskAssignmentId: flowTaskAssignmentId?.value,
          workflowAssignmentType: flowWorkflowAssignmentId?.RelatedAssignmentType,
          workflowAssignmentId: flowWorkflowAssignmentId?.value,
          dueDateAfter: flowElementDueDate,
          delayAfterCompletion: flowElementDelay,
          sortId: isNewElement ? flowElementData?.length : dataItem?.sortId,
          assignmentProjectId: assignmentProjectIdCondition,
          isApproval,
          isFlowLocked,
          flowDetail: flowWorkflowAssignmentId?.label,
        };
    return payload;
  };

  /**handles edit in case of existing flow element*/
  const handleFlowElementEdit = async (dataItem) => {
    let payload = createPayload(false, dataItem);
    let isNotValidInsertion, delgationBasedInsertion;
    payload["taskId"] = entity;
    if (dataItem.dynamicFlow) {
      update({ ...payload, id: dataItem?.id }, existingFlowElements, setFlowElementData, requiredFields(true));
      entity && (await editFlowElement(payload));
      return;
    }
    const updatedFlows = flowElements?.map((flow) => (flow?.id === dataItem?.id ? { ...flow, ...payload, id: dataItem?.id } : flow));
    const itemIndex = flowElements?.findIndex((flow) => flow.id === dataItem?.id);
    isNotValidInsertion = checkFlowInsertionFromFlows(updatedFlows, itemIndex - number.ONE, itemIndex + number.ONE, payload, false);
    !isNotValidInsertion ? dispatch(setFlowElements([...updatedFlows])) : getNotification(quote.CAN_NOT_COEXIST, notifyIcon?.WARNING_ICON);

    if (requiredFields(false) && !isNotValidInsertion && !delgationBasedInsertion) {
      update({ ...payload, id: dataItem?.id }, existingFlowElements, setFlowElementData, requiredFields(false));
      const response = entity && (await editFlowElement(payload));
      const newUpdatedFlows = flowElements?.map((flow) =>
        flow?.id === dataItem?.id ? { ...flow, ...payload, id: dataItem?.id, workflowIcon: response?.WorkflowIcon } : flow
      );
      dispatch(setFlowElements([...newUpdatedFlows]));
    }
  };

  const handleNewFlowElement = async (dataItem) => {
    const newDataItem = createPayload(true, dataItem);
    let payload = {
      workflowElements: [newDataItem],
      isNewTask,
    };
    payload["taskId"] = entity;
    const insertionIndex = taskElement ? flowElements?.length : flowElementData?.length - number.ONE;
    const response =
      entity &&
      requiredFields(dataItem.dynamicFlow) &&
        !checkFlowInsertionFromFlows(
        taskElement ? flowElements : flowElementData,
        insertionIndex - number.ONE,
        insertionIndex,
        newDataItem,
        taskElement ? false : true
      ) &&
      !(
        newDataItem.assignmentProjectId == projectId &&
        newDataItem.workflowAssignmentType == number.FIVE &&
        newDataItem.workflowAssignmentId == workflowId
      ) &&
      (await addFlowElement(payload));
    const id = entity ? response?.flowId : generateRandomId();
    handleTaskState({ ...newDataItem, workflowIcon: response?.WorkflowIcon }, requiredFields(dataItem.dynamicFlow), id, [...flowElements]);
  };

  return { handleFlowElementEdit, handleNewFlowElement };
};
